import {
  Box,
  Grid,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as OrderActions from "../../../../redux/orders/orders.actions";
import * as OrderReducers from "../../../../redux/orders/orders.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import { GetOrderStatusStyles } from "../../../../styles/OrderStatusStyle";
import { primary, secondary } from "../../../../themes/theme";
import SecondarySearchBar from "../../../../utils/common/secondary-search-bar/SecondarySearchBar";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";

const YourOrders: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();

  //Get Orders Data From Redux
  const orderReduxState: OrderReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[OrderReducers.orderFeatureKey];
    }
  );

  const { loading, orderProducts } = orderReduxState;

  useEffect(() => {
    dispatch(OrderActions.getMyOrdersAction());
  }, []);

  //Styles
  const cellStyle1 = {
    color: primary[800],
    fontWeight: "bold",
    fontFamily: "Ubuntu",
  };

  const cellStyle2 = {
    fontFamily: "Ubuntu",
    textTransform: "captilaize",
    Width: "100%",
  };

  return (
    <>
      <Box sx={{ mb: 2, pb: 2 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="My Orders"
          description="Track and manage your order/orders status, history, and details for a smooth purchasing experience."
        />

        {/* Search Bar  */}
        <SecondarySearchBar />

        <Grid
          container
          sx={{ pl: { xs: 1, md: 9, lg: 10 }, pr: { xs: 1, md: 2 } }}
        >
          {orderProducts && orderProducts.length > 0 ? (
            <>
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ borderRadius: "10px" }}
                >
                  <Table>
                    <TableHead sx={{ background: secondary[300] }}>
                      <TableRow>
                        <TableCell sx={{ ...cellStyle1 }}>Order ID</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>
                          Order Details
                        </TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>
                          Order Placed On
                        </TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Order By</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Total</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>
                          Payment Type
                        </TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>
                          Order Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderProducts.map((order) => {
                        return (
                          <TableRow key={order._id}>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 100,
                                maxWidth: 100,
                                minWidth: "100%",
                              }}
                            >
                              {order._id
                                ?.substring(order._id.length - 5)
                                .toUpperCase()}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 250,
                                minWidth: "100%",
                              }}
                            >
                              <List>
                                {order.products.map((product) => {
                                  return (
                                    <ListItem
                                      key={product._id}
                                      onClick={() =>
                                        navigate(
                                          `/products/view/Fashion/${product._id}`
                                        )
                                      }
                                      sx={{ cursor: "pointer", mx: 0, px: 0 }}
                                    >
                                      <Grid
                                        container
                                        sx={{
                                          display: "flex",
                                          justifyContent: {
                                            xs: "center",
                                            lg: "flex-start",
                                            height: "100%",
                                            alignItems: "center",
                                          },
                                        }}
                                        spacing={2}
                                      >
                                        <Grid item xs={12} lg={3}>
                                          <img
                                            src={product?.imageUrl}
                                            alt=""
                                            style={{
                                              width: "60px",
                                              height: "60px",
                                              borderRadius: "10px",
                                            }}
                                          />
                                        </Grid>
                                        <Grid item xs={12} lg={8}>
                                          <Typography
                                            style={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2, // Limit to 2 lines
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxHeight: "3em", // Adjust based on your line height
                                              maxWidth: "6em",
                                              lineHeight: "1.5em", // Adjust based on your preference
                                              fontFamily: "Ubuntu",
                                              fontWeight: "500",
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {product.title}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 170,
                                minWidth: "100%",
                              }}
                            >
                              {new Date(order.createdAt).toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              {order.orderBy?.username}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              &#8377;{Number(order.grandTotal).toFixed(2)}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              {order.paymentType}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 100,
                                minWidth: "100%",
                              }}
                            >
                              <Typography
                                sx={{
                                  textTransform: "capitalize",
                                  fontSize: "14px",
                                  fontFamily: "Ubuntu",
                                  py: "4px",
                                  fontWeight: "500",
                                  textAlign: "center",
                                  backgroundSize: "100%",
                                  borderRadius: 2,
                                  ...GetOrderStatusStyles(order.orderStatus),
                                }}
                              >
                                {order.orderStatus}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </>
          ) : (
            <Grid item xs={11}>
              <TableContainer component={Paper} elevation={1}>
                <Table>
                  <TableHead sx={{ background: secondary[300] }}>
                    <TableRow>
                      <TableCell sx={{ ...cellStyle1 }}>Order ID</TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>
                        Order Details
                      </TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>
                        Order Placed On
                      </TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>Order By</TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>Total</TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>Payment Type</TableCell>
                      <TableCell sx={{ ...cellStyle1 }}>Order Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={7} rowSpan={2}>
                        <Typography
                          sx={{ textAlign: "center", color: "orangered" }}
                        >
                          No Products
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default YourOrders;
